'use client';
import { createTheme } from '@mui/material/styles';
import CheckIcon from '@repo/ui/src/components/Icons/CheckIcon';
import palette from './palette';
import typography from './typography';

// Import direct hex values for MUI since it doesn't support CSS variables
const primary = palette.primary.main;           // #d95e16
const primaryMedium = palette.primary.dark;     // #b84308
const textColor = palette.text.primary;         // #25282a
const focusColor = palette.focus.main;          // #d95e16
const breakpointSm = '600px';
const headerFontFamily = "'Knockout', sans-serif";
const bannerFontFamily = "'Knockout', sans-serif";
const neutral = palette.neutral;
const warm = palette.warm;
const mediumGrey = palette.neutral[200];        // #d4d2ca
const lighterGrey = palette.warm[50];           // #f5f4f1

const MUITheme = createTheme({
  palette,
  // @ts-expect-error missing values
  typography,
  components: {
    MuiAutocomplete: {
      styleOverrides: {
        listbox: {
          '& li[aria-selected="true"].MuiAutocomplete-option, & li[aria-selected="true"].MuiAutocomplete-option.Mui-focused':
            {
              backgroundColor: 'rgba(0,0,0,0.04)',
            },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          fontFamily: headerFontFamily,
          fontSize: '1.5rem',
          lineHeight: '2.5rem',
          '&.Mui-focusVisible': {
            boxShadow: `0 0 1px 3px ${focusColor}`,
            outline: `3px solid ${focusColor}`,
          },
        },
        outlined: {
          borderColor: textColor,
          color: textColor,
          '&:hover': {
            color: `white !important`,
            borderColor: `${primary} !important`,
            backgroundColor: primary,
          },
        },
        outlinedSecondary: {
          '&:hover': {
            backgroundColor: primary,
            '& span': {
              color: 'white',
            },
          },
        },
        outlinedInherit: {
          color: 'inherit',
          borderColor: 'inherit',
        },
        sizeSmall: {
          fontSize: '1.2rem',
          lineHeight: '2rem',
        },
        contained: {
          '&.Mui-disabled': {
            backgroundColor: '#e1dedb',
          },
        },
        containedPrimary: {
          '&.processing&[disabled]': {
            backgroundColor: primaryMedium,
            color: 'white !important',
          },
          '&:hover': {
            color: 'white !important',
            backgroundColor: primaryMedium,
          },
          '& span:hover': {
            color: 'white !important',
          },
          '&[disabled]': {
            color: '#989898',
          },
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
        color: 'primary',
        checkedIcon: <CheckIcon color={warm[50]} svgFill={primary} />,
        icon: (
          <span
            style={{
              borderRadius: 0,
              width: 24,
              height: 24,
              boxShadow: `inset 0 0 0 2px ${neutral[500]}`,
              backgroundColor: '#ffffff',
            }}
          />
        ),
      },
      styleOverrides: {
        root: {
          padding: '8px',
          '&:hover': {
            backgroundColor: 'transparent !important',
          },
          '&.Mui-focusVisible': {
            outline: `3px solid ${focusColor}`,
          },
        },
        colorPrimary: {
          '&.Mui-focusVisible': {
            color: primary,
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: '-8px',
          color: neutral[600],
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: textColor,
          borderColor: textColor,
          fontSize: '18px',
          fontWeight: 'bold',
          '&.Mui-focused': {
            color: textColor,
            fontWeight: 'bold',
          },
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          '&:focus': {
            border: 0,
            outline: 'none',
            boxShadow: 0,
          },
          '&:active': {
            border: 0,
            outline: 'none',
            boxShadow: 0,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          '& legend': {
            // TODO: This is needed to override bootstrap _reboot.scss styles - Remove this after bootstrap is gone
            width: 0,
          },
        },
      },
    },
    MuiRadio: {
      defaultProps: {
        disableRipple: true,
        checkedIcon: (
          <span
            style={{
              borderRadius: '50%',
              width: 24,
              height: 24,
              border: `solid 7px ${primary}`,
              backgroundColor: '#ffffff',
            }}
          />
        ),
        icon: (
          <span
            style={{
              borderRadius: '50%',
              width: 24,
              height: 24,
              border: `solid 2px ${neutral[500]}`,
              backgroundColor: '#ffffff',
            }}
          />
        ),
      },
      styleOverrides: {
        root: {
          margin: '10px',
          '&:hover': {
            backgroundColor: 'transparent !important',
          },
          '&.Mui-focusVisible': {
            border: '0px',
          },
          '&:focus-visible': {
            marginLeft: '7px',
            marginRight: '7px',
          },
          '&:focus-visible > .MuiButtonBase-root': {
            border: `3px solid ${focusColor}`,
            borderRadius: '50%',
          },
        },
        colorPrimary: {
          '&.Mui-focusVisible': {
            color: primary,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        outlined: {
          '&:focus': {
            background: 'none',
          },
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          height: 6,
        },
        track: {
          height: 6,
          borderRadius: 2,
        },
        rail: {
          height: 6,
          borderRadius: 2,
          backgroundColor: mediumGrey,
          opacity: 1,
        },
        thumb: {
          height: 22,
          width: 22,
          border: `2px solid ${lighterGrey}`,
          '&:focus, &:hover, &.Mui-active': {
            boxShadow: 'inherit',
          },
        },
        mark: {
          width: 0,
        },
      },
    },
    MuiTab: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          minWidth: '0px !important',
          padding: 0,
          margin: '0 12px',
          fontFamily: bannerFontFamily,
          fontWeight: 'bold',
          fontSize: '1.8rem',
          [`@media (min-width: ${breakpointSm})`]: {
            fontSize: '2.4rem',
          },
          '&.Mui-focusVisible': {
            outline: `3px solid ${focusColor}`,
            outlineOffset: '5px',
          },
          '&.Mui-selected': {
            color: textColor,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: primary,
          height: 4,
          padding: 0,
          marginRight: 24,
          minWidth: '0px !important',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& legend': {
            // TODO: This is needed to override bootstrap _reboot.scss styles - Remove this after bootstrap is gone
            width: 0,
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        gutterBottom: {
          marginBottom: '1rem',
          [`@media (min-width: ${breakpointSm})`]: {
            marginBottom: '1.5rem',
          },
        },
      },
    },
  },
});

export default MUITheme;
